import React, { memo, useRef } from "react";
import { RCCGrid, RCCIcon } from "react-common-components";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import formatNumber from "src/utils/formatNumber";
import { v4 } from "uuid";
import { IChartTableIndicatorsValues, IListForChartModalIndicatorsComponent } from "../ModalIndicatorChart/interface";
import OverflowSpanBold, {ArrowLeft, ArrowRight, CardIndicatorPanel, DivAlignCenterWithOverflow, DivFlex, DivValues, IndicatorsPanelHeader, OverflowSpan} from "../ModalIndicatorChart/styles";

const SCROLL_AMOUNT = 152;

// DIFFERENCE BETWEEN, THIS COMPONENT USED IN MODAL VIEW CHART AND THE COMPONENT USED IN LIST, IS
// THAT THE COMPONENT USED IN LIST HAS RECURSIVITY, AND IS A BIT MORE COMPLEX FOR THAT.
// OF COURSE, IN THE FUTURE, BOTH COMPONENTS CAN BE TRANSFORMED IN 1, FOR NOW IT WILL KEEP THIS WAY

const ListForChartModalIndicators: React.FC<IListForChartModalIndicatorsComponent> = ({
	data,
	showAccumulatedValues,
	onClickChartAreaBarLine
}) => {
	const { translate, currentLanguage } = useTranslation();
	const indexOfCurrentPeriod = data[0].values.findIndex((dt) => dt.currentPeriod === dt.labelCode);
	const translateX = useRef<number>(indexOfCurrentPeriod > 12 ? -72 * indexOfCurrentPeriod : 0);
	const translateXAccumulated = useRef<number>(0);
	const divRef = useRef<HTMLDivElement>(null);

	const onClickHeaderTable = (periodId?: number) => {
		if(!onClickChartAreaBarLine || !periodId)
			return;
		onClickChartAreaBarLine(periodId, true);
	};

	const handleScrollValues = (direction: "left" | "right") => {
		if(!divRef.current)
			return;

		const divs = divRef.current.querySelectorAll(".indicators-values-panel-cards");

		if(direction === "right")
			translateX.current -= SCROLL_AMOUNT;
		else
			translateX.current += SCROLL_AMOUNT;

		for(let i = 0; i < divs.length; i+=1){
			const div = divs[i] as HTMLDivElement;
			div.style.transform = `translateX(${translateX.current}px)`;
		}
	};

	const handleScrollAccumulatedValues = (direction: "left" | "right") => {
		if(!divRef.current)
			return;

		const divs = divRef.current.querySelectorAll(".indicators-accumulated-values-panel-cards");

		if(direction === "right")
			translateXAccumulated.current -= SCROLL_AMOUNT;
		else
			translateXAccumulated.current += SCROLL_AMOUNT;

		for(let i = 0; i < divs.length; i+=1){
			const div = divs[i] as HTMLDivElement;
			div.style.transform = `translateX(${translateXAccumulated.current}px)`;
		}
	};

	const colIndicator = (row: any) => (
		<DivAlignCenterWithOverflow>
			<OverflowSpanBold title={row.name}> 
				{row.name} 
			</OverflowSpanBold>
		</DivAlignCenterWithOverflow>
	);

	const alertsItemList = (value: any) => (
		<>
			{value.comment && (
				// foi usado title, porque tooltip e popover não ficava bom
				<span title={value.comment}>
					<RCCIcon 
						color="var(--blue)"
						name="comment"
						size={12}
						style={{
							marginTop: "3px",
							marginLeft: "3px"
						}}
					/>
				</span>
			)}
		</>
	);

	const alertsItemHeader = (value: {
		hasFcaAndFcaAbsent?: boolean | string | null,
		hasFcaAndValue?: boolean | string | null,
		hasFca?:boolean | string | null
	}) => {

		if(value.hasFcaAndValue)
			return <RCCIcon 
				color="var(--green)"
				name="exclamationCircle"
				size={12}
				style={{
					marginLeft: "3px"
				}}
			/>;

		if(value.hasFcaAndFcaAbsent)
			return 	<RCCIcon 
				color="var(--orange)"
				name="exclamationCircle"
				size={12}
				style={{
					marginLeft: "3px"
				}}
			/>;

		if(value.hasFca)
			return <RCCIcon 
				name="exclamationCircle"
				size={12}
				style={{
					marginLeft: "3px"
				}}
			/>;

		return undefined;
	};

	const colValues = (row: any) => (
		<DivValues borderRight wrapInPrint={row.values && row.values.length > 16}>
			<RCCGrid 
				container
				item
				styleInline={`
					flex-wrap: unset !important;
					padding: 0 4px; 
					transform: translateX(${translateX.current}px);
					@media print{
						transform: unset;
					}
				`}
				justifyContent="space-between"
				className="indicators-values-panel-cards"
			>
				{row.values.map((value: IChartTableIndicatorsValues) => (
					<CardIndicatorPanel
						key={v4()}
						hasValue={value.value !== 0}
						currentPeriod={!!(value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod)}
						title={formatNumber(value.value, currentLanguage, value.decimalPlaces)}
						className={value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod ? "current-period" : "period"}
					>
						<OverflowSpan>
							{formatNumber(value.value, currentLanguage, value.decimalPlaces)}
						</OverflowSpan>
						{alertsItemList(value)}
					</CardIndicatorPanel>
     
				))}
			</RCCGrid>
		</DivValues>
	);

	const colAccumulatedValues = (row: any) => (
		<DivValues wrapInPrint={row.accumulatedValues && row.accumulatedValues.length > 5}>
			<RCCGrid 
				container
				item
				styleInline={`
					flex-wrap: unset !important; 
					padding: 0 4px; 
					transform: translateX(${translateXAccumulated.current}px);
					@media print{
						transform: unset;
					}
				`}
				className="indicators-accumulated-values-panel-cards"
			>
				{row.accumulatedValues && row.accumulatedValues.length > 0 && row.accumulatedValues.map((accumulatedValue: IChartTableIndicatorsValues) => (
					<CardIndicatorPanel
						key={v4()}
						hasValue={accumulatedValue.value > 0}
						currentPeriod={!!(accumulatedValue.labelCode && accumulatedValue.currentPeriod && accumulatedValue.labelCode === accumulatedValue.currentPeriod)}
						title={formatNumber(accumulatedValue.value, currentLanguage, accumulatedValue.decimalPlaces)}
						className={accumulatedValue.labelCode && accumulatedValue.currentPeriod && accumulatedValue.labelCode === accumulatedValue.currentPeriod ? "current-period" : "period"}
					>
						<OverflowSpan>
							{formatNumber(accumulatedValue.value, currentLanguage, accumulatedValue.decimalPlaces)}
						</OverflowSpan>
					</CardIndicatorPanel>
				))}
			</RCCGrid>
		</DivValues>
	);

	const customHeaderColValues = (rows: any) => {
		const firstRow = rows[0];

		const hasFcaAndFcaAbsent = (name: string) => rows?.some((row: any) => row.values?.some((e: any) => (e.name === name) && (!e.hasFca && e.fcaAbsent)));
		const hasFcaAndValue = (name: string) => rows?.some((row: any) => row.values?.some((e: any) => (e.name === name) && (e.value !== 0 && e.hasFca)));

		return(
			<DivValues borderRight wrapInPrint={firstRow.values && firstRow.values.length > 16}>
				<ArrowLeft className="arrowLeft-indicators">
					<RCCIcon 
						name="arrowLeft" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollValues("left")} 
					/>
				</ArrowLeft>
				<RCCGrid 
					container
					item
					styleInline={`
						flex-wrap: unset !important; 
						padding: 0 4px; 
						transform: translateX(${translateX.current}px);
						@media print{
							transform: unset;
						}
					`}
					justifyContent="space-between"
					className="indicators-values-panel-cards"
				>
					{firstRow.values.map((value: IChartTableIndicatorsValues) => (
						<IndicatorsPanelHeader
							key={v4()}
							currentPeriod={!!(value.labelCode && value.currentPeriod && value.labelCode === value.currentPeriod)}
							onClick={() => onClickHeaderTable(value.periodId)}
							style={onClickChartAreaBarLine ? { cursor: "pointer" } : {}}
						>
							<OverflowSpan title={value.name}>
								{value.name}
								{alertsItemHeader({
									hasFcaAndFcaAbsent: hasFcaAndFcaAbsent(value.name),
									hasFcaAndValue: hasFcaAndValue(value.name),
									hasFca: value.showFCAHeader
								})}
							</OverflowSpan>
						</IndicatorsPanelHeader>
					))}
				</RCCGrid>
				<ArrowRight className="arrowRight-indicators">
					<RCCIcon 
						name="arrowRight" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollValues("right")} 
					/>
				</ArrowRight>
			</DivValues>
		);};

	const customHeaderColAccumulatedValues = (rows: any) => {
		const firstRow = rows[0];
		return(
			<DivValues wrapInPrint={firstRow.accumulatedValues && firstRow.accumulatedValues.length > 5}>
				<ArrowLeft className="arrowLeft-indicators">
					<RCCIcon 
						name="arrowLeft" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollAccumulatedValues("left")} 
					/>
				</ArrowLeft>
				<RCCGrid 
					container
					item
					styleInline={`
						flex-wrap: unset !important; 
						padding: 0 4px; 
						transform: translateX(${translateXAccumulated.current}px);
						@media print{
							transform: unset;
						}
					`}
					className="indicators-accumulated-values-panel-cards"
				>
					{firstRow.accumulatedValues && firstRow.accumulatedValues.length > 0 && firstRow.accumulatedValues.map((accumulatedValue: IChartTableIndicatorsValues) => (
						<IndicatorsPanelHeader
							key={v4()}
							currentPeriod={!!(accumulatedValue.labelCode && accumulatedValue.currentPeriod && accumulatedValue.labelCode === accumulatedValue.currentPeriod)}
						>
							<OverflowSpan title={accumulatedValue.name}>
								{accumulatedValue.name}
							</OverflowSpan>
						</IndicatorsPanelHeader>
					))}
				</RCCGrid>
				<ArrowRight className="arrowRight-indicators">
					<RCCIcon 
						name="arrowRight" 
						color="var(--default-color-font)" 
						size={32}
						onClick={() => handleScrollAccumulatedValues("right")} 
					/>
				</ArrowRight>
			</DivValues>
		);};
   
	return(
		<DivFlex ref={divRef}>
			<List 
				data={data}
				columns={showAccumulatedValues ? 
					[{
						idColumn: "title",
						nameColumn: translate("Campos"),
						xs: 12,
						sm: 12,
						md: 2,
						justify: "left",
						truncate: true,
						cellRenderer: colIndicator
					},
					{
						idColumn: "values",
						nameColumn: translate("Valores"),
						xs: 12,
						sm: 12,
						md: 8,
						cellRenderer: colValues,
						customHeader: customHeaderColValues
					}, 
					{
						idColumn: "accumulatedValues",
						nameColumn: translate("Valores acumulados"),
						xs: 12,
						sm: 12,
						md: 2,
						cellRenderer: colAccumulatedValues,
						customHeader: customHeaderColAccumulatedValues
					}]
					: [{
						idColumn: "title",
						nameColumn: translate("Campos"),
						xs: 12,
						sm: 12,
						md: 2,
						justify: "left",
						truncate: true,
						cellRenderer: colIndicator
					},
					{
						idColumn: "values",
						nameColumn: translate("Valores"),
						xs: 12,
						sm: 12,
						md: 10,
						cellRenderer: colValues,
						customHeader: customHeaderColValues
					}]}
				showHeader
				headerHiddenXs={false}
				layout="table"
				defaultNumberOfElements={20}
				manuallyToggleShowMore
				styleInline={`li.list-component-child {
						min-height: 42px;
						padding: 0;
						flex-wrap: unset;
					}
					li.list-component-header{
						font-size: 0.8rem;
						padding: 0;
						margin: 0;
						flex-wrap: unset;
					}
					@media(max-width: 959px){
					li{
						flex-direction: column !important;
						align-items: flex-start !important;
						div{
							justify-content: flex-start !important;
							min-height: 1.25rem;
						}
					}
					@media print{
						li{
							flex-direction: row !important;
						}
					}
				}`}
			/>
		</DivFlex>
	);};

export default memo(ListForChartModalIndicators);
